import {
    getViewSettingsSafely,
    ViewIdentifiable,
} from "../../models/common/ViewSettings";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {SelectComponentCapable} from "../../models/common/Form";
import {ApiReturnType} from "../../models/common/ApiReturnType";
import {AppState} from "../../reducers/RootReducer";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {getUrlParamsFromViewSettings, RouteMethods} from "../../config/RouteConfig";
import {eventNotificationError, eventNotificationSuccess} from "../common/EventNotificationActions";
import {  StateObservable, ofType } from "redux-observable";
import {switchMap, filter, map, catchError, mergeMap} from "rxjs/operators";
import { ajax } from "rxjs/ajax";
import {asyncScheduler, concat, Observable, of} from 'rxjs';
import {
    ProjectStagePhase,
    ProjectStagePhaseModule,
    ProjectStagePhaseShort
} from "../../models/projects/ProjectStagePhase";
import {Percent} from "../../models/accounting/Finance";

import {Performanceable} from "../../models/accounting/Performanceable";
import {ProjectStagePhaseIdPath, ProjectStagePhaseIdPathProp} from "../../models/projects/Project";
import {projectContractorNextIdError, projectContractorNextIdSuccess} from "./ProjectContractorActions";

export type ProjectStagePhaseViewActionTypes = FetchProjectStagesActionTypes | FetchProjectPhaseNextIdActionTypes


/**
 * Fetch phases by project and stage
 */
export interface FetchProjectStagePhasesAction extends ViewIdentifiable, ReduxAction< typeof FETCH_PROJECT_STAGE_PHASES >, SelectComponentCapable {
    projectId: number
    projectStageId: number
}
export interface FetchProjectStagePhasesSuccessAction extends ViewIdentifiable, ReduxAction<typeof FETCH_PROJECT_STAGE_PHASES_SUCCESS > {
    payload: ApiReturnType< ProjectStagePhase[] | ProjectStagePhaseShort[] >
}
export interface FetchProjectStagePhasesErrorAction extends ViewIdentifiable, ErrorAction< typeof FETCH_PROJECT_STAGE_PHASES_ERROR >{}

export type FetchProjectStagesActionTypes = FetchProjectStagePhasesAction | FetchProjectStagePhasesErrorAction | FetchProjectStagePhasesSuccessAction


export const FETCH_PROJECT_STAGE_PHASES = "FETCH_PROJECT_STAGE_PHASES";
export const FETCH_PROJECT_STAGE_PHASES_SUCCESS = "FETCH_PROJECT_STAGE_PHASES_SUCCESS";
export const FETCH_PROJECT_STAGE_PHASES_ERROR = "FETCH_PROJECT_STAGE_PHASES_ERROR";

export const fetchProjectStagePhases = ( viewIdentifier: string, projectId: number, projectStageId: number, isFetchForSelect: boolean = false ) : FetchProjectStagePhasesAction =>
    ({ type: FETCH_PROJECT_STAGE_PHASES, viewIdentifier, isFetchForSelect, projectId, projectStageId });
export const fetchProjectStagePhasesSuccess = (viewIdentifier: string, payload: ApiReturnType< ProjectStagePhase[] | ProjectStagePhaseShort[] > ) : FetchProjectStagePhasesSuccessAction =>
    ({ type: FETCH_PROJECT_STAGE_PHASES_SUCCESS, viewIdentifier, payload });
export const fetchProjectStagePhasesError = ( viewIdentifier: string, error: Error ): FetchProjectStagePhasesErrorAction =>
    ({ type: FETCH_PROJECT_STAGE_PHASES_ERROR, viewIdentifier, error });

export function fetchProjectStagePhasesEpic(
    action$: Observable< FetchProjectStagePhasesAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(FETCH_PROJECT_STAGE_PHASES),
        filter(action => action.viewIdentifier === ProjectStagePhaseModule.identifier),
        switchMap(action => {
            const viewSettings = getViewSettingsSafely(action.viewIdentifier, store$);
            const path = `${APP_CONFIG.baseWeb}projects/${action.projectId}/stages/${action.projectStageId}/phases/${getUrlParamsFromViewSettings(viewSettings)}`;
            return ajax(APP_CONFIG.getRequestObject(path)).pipe(
                map(response => fetchProjectStagePhasesSuccess(
                    action.viewIdentifier,
                    response.response as ApiReturnType< ProjectStagePhase[] >
                )),
                catchError(error => of(
                    fetchProjectStagePhasesError(action.viewIdentifier, error),
                    eventNotificationError( error , error.status, action.viewIdentifier, "fetchProjectStagePhases")
                ))
            );

        })
    );
}

/**
 * UPDATE phase performance
 */
export interface UpdatePhasePerformanceAction extends Performanceable, ProjectStagePhaseIdPathProp, ReduxAction< typeof UPDATE_PHASE_PERFORMANCE> {}
export interface UpdatePhasePerformanceSuccessAction extends Performanceable, ProjectStagePhaseIdPathProp, ReduxAction<typeof UPDATE_PHASE_PERFORMANCE_SUCCESS> {}
export interface UpdatePhasePerformanceErrorAction  extends ProjectStagePhaseIdPathProp, ErrorAction<typeof UPDATE_PHASE_PERFORMANCE_ERROR> {}

export type UpdatePhasePerformanceActionTypes = UpdatePhasePerformanceAction | UpdatePhasePerformanceSuccessAction | UpdatePhasePerformanceErrorAction

export const UPDATE_PHASE_PERFORMANCE = "UPDATE_PHASE_PERFORMANCE";
export const UPDATE_PHASE_PERFORMANCE_SUCCESS = "UPDATE_PHASE_PERFORMANCE_SUCCESS";
export const UPDATE_PHASE_PERFORMANCE_ERROR = "UPDATE_PHASE_PERFORMANCE_ERROR";

export function updatePhasePerformance(
    phaseIdPath: ProjectStagePhaseIdPath,
    performance: Percent
): UpdatePhasePerformanceAction {
    return { phaseIdPath, type: UPDATE_PHASE_PERFORMANCE, performance };
}

export function updatePhasePerformanceSuccess(
    phaseIdPath: ProjectStagePhaseIdPath,
    performance: Percent
): UpdatePhasePerformanceSuccessAction {
    return { type: UPDATE_PHASE_PERFORMANCE_SUCCESS, phaseIdPath, performance };
}

export function updatePhasePerformanceError(
    phaseIdPath: ProjectStagePhaseIdPath,
    error: any
): UpdatePhasePerformanceErrorAction {
    return { type: UPDATE_PHASE_PERFORMANCE_ERROR, phaseIdPath, error };
}

export function updatePhasePerformanceEpic(
    action$: Observable< UpdatePhasePerformanceAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( UPDATE_PHASE_PERFORMANCE ),
        switchMap( action => {
            const { projectId, projectStageId, phaseId } = action.phaseIdPath;
            const url = APP_CONFIG.baseWeb + `projects/${ projectId.rawValue }/stages/${ projectStageId.rawValue }/phases/${ phaseId.rawValue }/${ action.performance }`;

            return ajax( APP_CONFIG.getRequestObject( url, RouteMethods.patch ) ).pipe(
                mergeMap( _ => of(
                    updatePhasePerformanceSuccess( action.phaseIdPath, action.performance ),
                    eventNotificationSuccess()
                )),
                catchError( error => of(
                    updatePhasePerformanceError( action.phaseIdPath, error ),
                    eventNotificationError( error, store$, ProjectStagePhaseModule.identifier, "updatePhasePerformance" )
                ) )
            );
        })
    );
}

/**
 * FETCH NEXT ID
 */
export interface FetchProjectPhaseNextIdAction extends ReduxAction< typeof PROJECT_PHASE_NEXT_ID> {

}

export interface FetchProjectPhaseNextIdSuccessAction extends ReduxAction<typeof PROJECT_PHASE_NEXT_ID_SUCCESS > {
    payload: number
}
export interface FetchProjectPhaseNextIdErrorAction extends ErrorAction<typeof PROJECT_PHASE_NEXT_ID_ERROR> {

}

export type FetchProjectPhaseNextIdActionTypes = FetchProjectPhaseNextIdAction
    | FetchProjectPhaseNextIdSuccessAction
    | FetchProjectPhaseNextIdErrorAction

export const PROJECT_PHASE_NEXT_ID = "PROJECT_PHASE_NEXT_ID";
export const PROJECT_PHASE_NEXT_ID_SUCCESS = "PROJECT_PHASE_NEXT_ID_SUCCESS";
export const PROJECT_PHASE_NEXT_ID_ERROR = "PROJECT_PHASE_NEXT_ID_ERROR";

export function fetchProjectPhaseNextId(): FetchProjectPhaseNextIdAction {
    return { type: PROJECT_PHASE_NEXT_ID };
}
export function fetchProjectPhaseNextIdSuccess( payload: number ): FetchProjectPhaseNextIdSuccessAction {
    return { type: PROJECT_PHASE_NEXT_ID_SUCCESS, payload };
}
export function fetchProjectPhaseNextIdError( error: any ): FetchProjectPhaseNextIdErrorAction {
    return { type: PROJECT_PHASE_NEXT_ID_ERROR, error };
}

export function fetchProjectPhaseNextIdEpic(
    action$: Observable<FetchProjectPhaseNextIdAction>,
    store$: StateObservable<AppState>
) {
    return action$.pipe(
        ofType( PROJECT_PHASE_NEXT_ID ),
        switchMap( action => {
            const path = APP_CONFIG.baseWeb + 'projects/phases/next-id';

            return ajax(
                APP_CONFIG.getRequestObject( path )
            ).pipe(
                map( response => fetchProjectPhaseNextIdSuccess( response.response as number )),
                catchError( error => concat(
                        of( fetchProjectPhaseNextIdError( error ) ),
                        of( eventNotificationError( error, store$, "project_phases", "nextId" ) ),
                        asyncScheduler
                    )
                )
            )
        } )
    );
}
